import tokenApiClient from "@/services/auth/token-api-client";
import {
  generateScope,
  getSubdomain,
  organisationToSubdomain,
  persistPageBeforeLogin,
} from "@/utils";
import qs from "qs";
import authHelpers from "@/services/auth/helpers";
import JwtDecode from "jwt-decode";
import moment, { Moment } from "moment";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import env from "@/env";
import jwtDecode from "jwt-decode";
export enum AuthTokenType {
  IdToken,
  AccessToken,
  AppOrUserAccessToken,
}

declare global {
  interface Window {
    cachedAuthBearer: string | null;
    cachedOrgToken: string | null;
    authBearerPromise: Promise<string | null> | null;
    orgTokenPromise: Promise<string | null> | null;
  }
}


export const getAppAccessToken = async () => {
  const body = qs.stringify({
    grant_type: "client_credentials",
  });
  const response = await tokenApiClient.post("", body);
  return response?.data;
};

export const getRefreshToken = async (refreshToken: string) => {
  const url = `${env.api.baseUrl.tokenBaseUrl}oauth2/token`;

    const body = new URLSearchParams();
    body.append("grant_type", "refresh_token");
    body.append("client_id", env.app.authCodeAppId);
    body.append("refresh_token", refreshToken);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const content = await response.json();
    return content;
};

export const forceLogout = () => {
  authHelpers.clearTokens();
  persistPageBeforeLogin();
  window.location.href = "/";
};

export const isTokenExpired = (token: string) => {
  const decodedToken = JwtDecode(token) as { exp: number };
  return moment().isAfter(moment.unix(decodedToken.exp));
};

export const fetchOrganisationToken = async (
  accessToken: string,
  forceFetching: boolean = false
): Promise<string | null> => {
  const cookieOrgToken = authHelpers.getOrgToken();
  if (cookieOrgToken) {
    window.cachedOrgToken = cookieOrgToken;
  }

  if (
    window.cachedOrgToken &&
    !isTokenExpired(window.cachedOrgToken) &&
    !forceFetching
  ) {
    return window.cachedOrgToken;
  }

  if (window.orgTokenPromise) {
    return window.orgTokenPromise;
  }

  window.orgTokenPromise = (async () => {
    try {
      const {
        data: { data },
      } = await axios.get(`${env.api.baseUrl.membership}/users/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const subdomain = getSubdomain();
      const organisationCurrent = data?.memberships.find(
        (org: { organisationName: string }) => {
          return organisationToSubdomain(org.organisationName) === subdomain;
        }
      );

      if (!organisationCurrent) {
        const organisationUser = data?.memberships?.filter(
          (org: { roleName: string }) =>
            org.roleName?.toLowerCase() ===
            env.roles.organisationOwner.toLowerCase()
        );
        const memberShip =
          organisationUser?.length > 0
            ? organisationUser[0]
            : data?.memberships[0];
        window.cachedOrgToken = memberShip.token;
      } else {
        window.cachedOrgToken = organisationCurrent.token;
      }

      window.orgTokenPromise = null; // Reset promise
      return window.cachedOrgToken;
    } catch (error) {
      window.orgTokenPromise = null; // Reset promise in case of error
      return null;
    }
  })();

  return window.orgTokenPromise;
};

let appAccessToken: string;
let appAccessExpriedToken: Moment | undefined;
export const fetchAppAccessToken = async () => {
  if (
    !appAccessToken ||
    (appAccessExpriedToken && moment().isSameOrAfter(appAccessExpriedToken))
  ) {
    const response = await getAppAccessToken();
    appAccessToken = response?.access_token;
    appAccessExpriedToken = moment().add(response?.expires_in, "s");
  }
  return appAccessToken;
};

export const getAuthBearer = async (
  authTokenType: AuthTokenType
): Promise<string | null> => {
  // Return cached token if available
  if (window.cachedAuthBearer && !isTokenExpired(window.cachedAuthBearer)) {
    return window.cachedAuthBearer;
  }

  // If a token fetch is already in progress, return the same promise
  if (window.authBearerPromise) {
    return window.authBearerPromise;
  }

  // Create a new promise for fetching the token
  window.authBearerPromise = (async () => {
    let token: string | null = null;
    const accessToken = await authHelpers.getAccessToken();
    switch (authTokenType) {
      case AuthTokenType.AccessToken:
        token = await accessToken;
        break;
      case AuthTokenType.IdToken:
        token = await authHelpers.getIdToken();
        break;
      case AuthTokenType.AppOrUserAccessToken:
        token = accessToken || (await fetchAppAccessToken());
        break;
      default:
        token = null;
    }
    // Cache the fetched token
    window.cachedAuthBearer = token;

    // Reset the promise after the fetch is complete
    window.authBearerPromise = null;
    return token;
  })();
  return window.authBearerPromise;
};

export const refreshTokens = async () => {
  const refreshToken = await authHelpers.getRefreshToken();

  if (!refreshToken) {
    throw new Error("No refresh token found");
  }

  const { refresh_token, id_token, access_token } = await getRefreshToken(
    refreshToken ?? ""
  );
  // console.log('refresh', withOrgToken, orgToken, baseURL);

  // if (withOrgToken) {
  const orgToken = await fetchOrganisationToken(access_token, true);
  authHelpers.storeOrgToken(orgToken ?? "");
  // }

  authHelpers.storeIdToken(id_token);
  authHelpers.storeAccessToken(access_token);
  if (refresh_token) {
    authHelpers.storeRefreshToken(refresh_token);
  }
  return {
    accessToken: access_token,
    orgToken,
  };
};
type TokenSubscriberCallback = (
  error: Error | null,
  accessToken?: string,
  orgToken?: string
) => void;

let tokenSubscribers: TokenSubscriberCallback[] = [];

export const subscribeTokenRefresh = (cb: TokenSubscriberCallback) => {
  tokenSubscribers.push(cb);
};

export const unsubscribeTokenRefresh = () => {
  tokenSubscribers = [];
}

export const onTokenRefreshed = (
  error: Error | null,
  newAccessToken?: string,
  orgToken?: string
) => {
  tokenSubscribers.forEach((cb) => cb(error, newAccessToken, orgToken));
};

export const createOnRequest = (authTokenType: AuthTokenType, withOrgToken: boolean) => {
  const onRequest = async (request: AxiosRequestConfig) => {
    const authBearer = await getAuthBearer(authTokenType);
    let isFetchOrganisationToken = true;

    if (!authBearer) {
      isFetchOrganisationToken = false;
    } else {
      const decodedToken = jwtDecode(authBearer) as { aut: string };
      if (decodedToken.aut === "APPLICATION") {
        isFetchOrganisationToken = false;
      }
    }
    const orgToken =
      withOrgToken && isFetchOrganisationToken && authBearer
        ? await fetchOrganisationToken(authBearer)
        : null;
    if (authBearer) {
      request.headers.Authorization = `Bearer ${authBearer}`;
    }
    if (orgToken) {
      request.headers["org-token"] = orgToken;
    }

    return request;
  };

  return onRequest;
}
let isRefreshingAccessToken = false;

 export const onResponseSuccess = (response: AxiosResponse) => response;

 export const onResponseError = async (axiosError: AxiosError) => {
    const { response } = axiosError;
    const originalRequest = axiosError.config;

    const responseStatus = response?.status;
    if (responseStatus === 401) {
      if (!isRefreshingAccessToken) {
        isRefreshingAccessToken = true;
        window.cachedAuthBearer = null;
        window.cachedOrgToken = null;
        refreshTokens()
          .then(({ accessToken, orgToken }) => {
            onTokenRefreshed(null, accessToken ?? "", orgToken ?? "");
          })
          .catch(() => {
            onTokenRefreshed(new Error("Failed to refresh access token"));
            forceLogout();
          })
          .finally(() => {
            isRefreshingAccessToken = false;
            unsubscribeTokenRefresh();
          });
      }

      const retryOriginalRequest = new Promise((resolve, reject) => {
        subscribeTokenRefresh(
          (error: Error | null, newAccessToken?: string, orgToken?: string) => {
            if (error) return reject(error);

            if (orgToken) {
              originalRequest.headers["org-token"] = orgToken;
            }
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

            return resolve(axios(originalRequest));
          }
        );
      });
      return retryOriginalRequest;
    }

    return Promise.reject(axiosError);
  };